export type HandoffEventKind = 'requested' | 'offered' | 'accepted' | 'no_response' | 'no_agent'

export interface HandoffEvent {
  id: string
  kind: HandoffEventKind
  at: string
  agentName: string | null
}

/**
 * Events are sorted by time. Walk the messages in order and, before each
 * message, render every event not yet shown that happened at or before it.
 * After the last message render whatever remains (`atMs = Infinity`).
 */
export function takeEventsUpTo(
  events: HandoffEvent[],
  cursor: number,
  atMs: number,
): { taken: HandoffEvent[]; cursor: number } {
  let i = cursor
  const taken: HandoffEvent[] = []
  while (i < events.length && Date.parse(events[i]!.at) <= atMs) {
    taken.push(events[i]!)
    i++
  }
  return { taken, cursor: i }
}

/**
 * Positions handoff events among a thread's messages (already in time order):
 * `before` maps a message id to the events to render just above it, `trailing`
 * holds events later than every message. Threads that group messages by ticket
 * use the map so a marker stays next to the message it precedes, whatever
 * group that message ends up in.
 */
export function placeHandoffEvents(
  messages: Array<{ id: string; sent_at: string }>,
  events: HandoffEvent[],
): { before: Map<string, HandoffEvent[]>; trailing: HandoffEvent[] } {
  const before = new Map<string, HandoffEvent[]>()
  let cursor = 0
  for (const msg of messages) {
    const upTo = takeEventsUpTo(events, cursor, Date.parse(String(msg.sent_at)))
    cursor = upTo.cursor
    if (upTo.taken.length > 0) before.set(msg.id, upTo.taken)
  }
  return { before, trailing: takeEventsUpTo(events, cursor, Number.POSITIVE_INFINITY).taken }
}
